import React, { useEffect, useState, useRef } from 'react'
import styles from './header.module.css'
import Navbar from '../Navbar/Navbar'
import { Cart, Location, Profile, Topdropdown } from '../../../../Svg'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import api from '../../../utils/api'
import { stripHtmlTags } from '../../../utils/utils'
import { Bell, Moon, Sun } from 'lucide-react'
import { fetchMainSearch, formatStock, getExcessManufacturer } from '../../../utils/mainSearch'
import { setExcessSearch } from '../../../redux/excessSlice'
import { startDarkPurpleWatch, stopDarkPurpleWatch, refreshDarkPurple } from '../../../utils/darkPurple'

const Header = () => {
    const { customer, refresh, loading, isLoggedin } = useSelector((state) => state.customer)
    const { notificationCountData } = useSelector((state) => state.notification)
    const { scheduleData, refresh: scheduleRefresh } = useSelector((state) => state.schedule)
    const navigate = useNavigate();
    const location = useLocation();
    const isHomePage = location.pathname === "/";
    const [scrolled, setScrolled] = useState(false);
    const [scheduleCount, setScheduleCount] = useState(0);

    // Site-wide dark mode toggle. Remembered across visits; the actual dark
    // styling lives in index.css under html.ew-dark.
    const [darkMode, setDarkMode] = useState(() => {
        try { return localStorage.getItem('ew-theme') === 'dark'; } catch { return false; }
    });
    useEffect(() => {
        const root = document.documentElement;
        if (darkMode) root.classList.add('ew-dark'); else root.classList.remove('ew-dark');
        try { localStorage.setItem('ew-theme', darkMode ? 'dark' : 'light'); } catch {}
        if (darkMode) startDarkPurpleWatch(); else stopDarkPurpleWatch();
    }, [darkMode]);

    // Re-tag purple elements after each route change (new page content)
    useEffect(() => {
        if (!darkMode) return;
        const t = setTimeout(refreshDarkPurple, 60);
        return () => clearTimeout(t);
    }, [darkMode, location.pathname]);

    // Items stay counted until the schedule cart is submitted
    useEffect(() => {
        if (isLoggedin !== true) {
            setScheduleCount(0);
            return;
        }
        let cancelled = false;
        api.getScheduleDetails()
            .then(({ data }) => {
                if (!cancelled) setScheduleCount(data?.data?.length || 0);
            })
            .catch((err) => console.log(err));
        return () => { cancelled = true; };
    }, [isLoggedin, customer, scheduleData, scheduleRefresh, location.pathname]);
    
    // Header search states
    const [search, setSearch] = useState('')
    const [suggestedData, setSuggestedData] = useState([])
    const [excessData, setExcessData] = useState([])
    const searchRef = useRef(null);
    const dispatch = useDispatch();

    const clearSuggestions = () => {
        setSuggestedData([]);
        setExcessData([]);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                clearSuggestions();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const getHeroThreshold = () => {
            const heroSearchEl = document.querySelector('[class*="searchWrapper"]');
            if (heroSearchEl) {
                const rect = heroSearchEl.getBoundingClientRect();
                return window.scrollY + rect.bottom;
            }
            return 380;
        };

        const handleScroll = () => {
            const threshold = isHomePage ? getHeroThreshold() : 0;
            if (window.scrollY > threshold) {
                setScrolled(true);
            } else {
                setScrolled(false);
                clearSuggestions();
            }
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isHomePage]);

    const searchCategory = (e) => {
        if (e) e.preventDefault()
        clearSuggestions()
        if (search && search.trim()) {
            navigate(`/categories?search=${encodeURIComponent(search.trim())}`)
        }
    }

    const latestQueryRef = useRef('');
    const debounceTimerRef = useRef(null);

    const smartSearch = (text) => {
        setSearch(text);
        latestQueryRef.current = text;
        if (!text.trim()) {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            clearSuggestions();
            return;
        }
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
            const currentQuery = text.trim();
            try {
                const { suggestions, excessItems } = await fetchMainSearch(currentQuery);
                if (latestQueryRef.current.trim() === currentQuery) {
                    setSuggestedData(suggestions);
                    setExcessData(excessItems);
                }
            } catch (err) {
                console.log(err);
            }
        }, 180);
    };

    const getCleanUrl = (url) => {
        if (!url) return '';
        if (url.includes('asset.electrowiz.in')) {
            return url.replace('https://asset.electrowiz.in', 'https://electrowiz-ap.s3.ap-south-1.amazonaws.com');
        }
        return url;
    };

    const handlecatclick = (data) => {
        clearSuggestions()
        if (data?.type === "level3") {
            navigate(`/productList/${data?.name}`)
        } else {
            navigate(`/categories?search=${encodeURIComponent(data?.name)}`)
        }
    }

    // Excess results open the Excess Inventory page searched for that part
    const handleExcessClick = (item) => {
        clearSuggestions()
        dispatch(setExcessSearch(item?.productNumber || ''))
        navigate('/exInventoryBuyItem')
    }

    const showHeaderSearch = !isHomePage || scrolled;

    return (
        <div className={`${styles.headerContainer} ${(scrolled || !isHomePage) ? styles.scrolled : ""}`}>
            <div className={styles.headerRow2}>
                <div className={styles.headLogo} onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                    <img src='/assets/logo.png' alt="Electrowiz Logo" />
                </div>
                
               
                    {showHeaderSearch && (<form ref={searchRef} onSubmit={searchCategory} className={styles.headerSearchContainer}>
                        <div className={styles.inputWrapper}>
                            <span className={styles.headerSearchIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </span>
                            <input
                                type="text"
                                placeholder="Search parts, manufacturers..."
                                className={styles.headerSearchInput}
                                value={search}
                                onChange={(e) => smartSearch(e.target.value)}
                            />
                        </div>

                        
                        {(suggestedData.length > 0 || excessData.length > 0) && (
                            <div className={styles.headerSearchBox}>
                                <ul>
                                    {suggestedData.map((i, index) => (
                                        <React.Fragment key={index}>
                                            {i?.type !== "product" ? (
                                                <li onClick={() => handlecatclick(i)}>
                                                    <span className={styles.headerSuggestionIcon}>📦</span>
                                                    <div className={styles.headerSuggestionMeta}>
                                                        <span className={styles.headerSuggestionTitle}>{i?.name}</span>
                                                    </div>
                                                </li>
                                            ) : (
                                                <li onClick={() => navigate(`/ProductCode/${i?.name}`)}>
                                                    {i?.image ? (
                                                        <img src={getCleanUrl(i.image)} alt={i.name} className={styles.headerSuggestionImage} />
                                                    ) : (
                                                        <span className={styles.headerSuggestionIcon}>⚡</span>
                                                    )}
                                                    <div className={styles.headerSuggestionMeta}>
                                                        <span className={styles.headerSuggestionTitle}>
                                                            Part: {i?.name}{(i?.manufacturer?.[0]?.name || i?.productOverview?.manufacturer?.[0]?.name) ? ` | ${i?.manufacturer?.[0]?.name || i?.productOverview?.manufacturer?.[0]?.name}` : ''}
                                                        </span>
                                                        {(i?.description || i?.productOverview?.description) && (
                                                            <span className={styles.headerSuggestionDescription}>
                                                                {stripHtmlTags(i?.description || i?.productOverview?.description)}
                                                            </span>
                                                        )}
                                                        {i?.inventory?.qtyAvailable !== undefined && (
                                                            <span className={styles.headerSuggestionStock}>{formatStock(i?.inventory?.qtyAvailable)}</span>
                                                        )}
                                                    </div>
                                                </li>
                                            )}
                                        </React.Fragment>
                                    ))}
                                {excessData.length > 0 && (
                                    <>
                                        <li className={styles.headerSuggestionSection}>Available in Excess Inventory</li>
                                        {excessData.map((item) => {
                                            const mfg = getExcessManufacturer(item);
                                            return (
                                                <li key={`excess-${item?._id}`} onClick={() => handleExcessClick(item)}>
                                                    <span className={styles.headerSuggestionIcon}>♻️</span>
                                                    <div className={styles.headerSuggestionMeta}>
                                                        <span className={styles.headerSuggestionTitle}>
                                                            Part: {item?.productNumber}{mfg ? ` | ${mfg}` : ''}
                                                        </span>
                                                        <span className={styles.headerSuggestionStock}>{formatStock(item?.stock)}</span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </>
                                )}
                                </ul>
                            </div>
                        )}
                    </form>
                )}
                
                <div className={styles.headLangAndIcons}>
                    
                    <div className={styles.headLang}>
                        <div>
                            <span>₹ INR</span>
                            <Topdropdown />
                        </div>
                        {isLoggedin === true ? (
                            <div className={styles.userName} onClick={() => navigate('/myprofile')}>
                                {customer?.fullName}
                            </div>
                        ) : (
                            <div onClick={() => navigate('/login')} className={styles.loginBtn}>
                                <span>Sign In</span>
                            </div>
                        )}
                    </div>

                  
                    <div className={styles.headIcons}>

                        <div onClick={() => setDarkMode((v) => !v)} className={styles.iconBtn} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                            {darkMode
                                ? <Sun size={20} color="#ffffff" strokeWidth={2.2} />
                                : <Moon size={20} color="#ffffff" strokeWidth={2.2} />}
                        </div>

                        {isLoggedin === true && (
                            <div onClick={() => navigate('/userNotifications')} className={styles.iconBtn}>
                                <Bell size={20} color="#ffffff" strokeWidth={2.2} />
                                {notificationCountData > 0 && (
                                    <div className={styles.badge}>
                                        {notificationCountData}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div onClick={() => navigate('/cart')} className={styles.iconBtn}>
                            <img src='/assets/shoppingcart.png' alt="Cart" />
                            {customer?.unreadCartCount > 0 && (
                                <div className={styles.badge}>
                                    {customer?.unreadCartCount}
                                </div>
                            )}
                        </div>
                        
                        <div onClick={() => navigate('/scheduleCart')} className={styles.iconBtn}>
                            <img src='/assets/schedulecart.png' alt="Schedule Cart" />
                            {scheduleCount > 0 && (
                                <div className={styles.badge}>
                                    {scheduleCount}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Navbar />
        </div>
    )
}

export default Header
